/**
 * Primary Product Category Mapper
 * Maps product categories to standardized Primary Product Category names
 * for Leadsquared and WebEngage integration
 * 
 * Standardized Categories:
 * - Mattresses (Exclude AI Mattress)
 * - Beds
 * - Pillow (Exclude AI Pillow)
 * - Chairs (Exclude Bulk Order)
 * - Bedding
 * - Recliner Sofa
 * - Desk
 * - Cushion
 * - AI Sleep Solution
 * - AI mattress
 * - AI Pillow
 * - Elite Recliner Sofa
 * - Massager
 */

/**
 * Maps product categories to standardized Primary Product Category names
 * @param {string} productType - The product type from Shopify (e.g., "Mattresses", "Chairs")
 * @param {string} productTitle - The product title (to check for AI products)
 * @param {string} collectionHandle - The collection handle (optional)
 * @param {string|number} productId - The product ID (optional, for elite products)
 * @returns {string} - Standardized Primary Product Category name
 */
function getPrimaryProductCategory(productType, productTitle, collectionHandle, productId) {
  if (!productType) {
    return '';
  }

  const typeLower = productType.toLowerCase().trim();
  const titleLower = (productTitle || '').toLowerCase().trim();
  const collectionLower = (collectionHandle || '').toLowerCase().trim();

  // Check for AI products first (these have specific categories)
  if (titleLower.includes('ai mattress') || titleLower.includes('sens-ai') || titleLower.includes('sensai')) {
    return 'AI mattress';
  }
  
  if (titleLower.includes('ai pillow')) {
    return 'AI Pillow';
  }
  
  if (titleLower.includes('ai sleep solution') || titleLower.includes('ai sleep')) {
    return 'AI Sleep Solution';
  }

  // Elite Recliner Sofa - check by product ID or collection
  const eliteProductsArray = ["9020286992613", "9024321159397", "9024320700645", "9584619782373", "9234386256101", "9584618504421"];
  if (productId && eliteProductsArray.includes(String(productId))) {
    return 'Elite Recliner Sofa';
  }
  
  if (collectionLower === 'sofas' || collectionLower === 'elite-sofa') {
    return 'Elite Recliner Sofa';
  }

  // Map product types to standardized categories
  // Mattresses (Exclude AI Mattress - already handled above)
  if (typeLower.includes('mattress') && !titleLower.includes('ai')) {
    return 'Mattresses';
  }

  // Beds
  if (typeLower.includes('bed') || typeLower === 'beds') {
    return 'Beds';
  }

  // Pillow (Exclude AI Pillow - already handled above)
  if (typeLower.includes('pillow') && !titleLower.includes('ai')) {
    return 'Pillow';
  }

  // Chairs (Exclude Bulk Order - this is handled at source level)
  if (typeLower.includes('chair') || typeLower === 'chairs') {
    return 'Chairs';
  }

  // Bedding
  if (typeLower.includes('bedding') || typeLower.includes('bed sheet') || typeLower.includes('comforter')) {
    return 'Bedding';
  }

  // Recliner Sofa (non-elite)
  if (typeLower.includes('recliner') || typeLower.includes('sofa')) {
    return 'Recliner Sofa';
  }

  // Desk
  if (typeLower.includes('desk') || typeLower === 'desk') {
    return 'Desk';
  }

  // Cushion
  if (typeLower.includes('cushion') || typeLower === 'cushions') {
    return 'Cushion';
  }

  // Massager
  if (typeLower.includes('massager') || typeLower.includes('massage')) {
    return 'Massager';
  }

  // Default: return empty string if no match
  return '';
}

// Export for global access
if (typeof window !== 'undefined') {
  window.getPrimaryProductCategory = getPrimaryProductCategory;
}
